import type {
  MeasuredChallengeRules,
  MeasuredAttempts,
} from "../challenge/measured-types.js";
import type {
  MainContestInput,
  ContestSnapshot,
} from "../contest/contest-types.js";
import type {
  ResultCorrectionProposal,
  ResultVersion,
} from "../result/result-correction-types.js";
import type { SimulatorRoundSettings } from "../simulator/simulator-types.js";
import type { ImportedRoundTotals } from "../imported-round/imported-totals-types.js";
import type { HoleBonus } from "../contest/hole-bonus.js";

export type RoundState =
  "draft" | "active" | "completed" | "disputed" | "voided" | "abandoned";

/** A round that can still be played: created, but not yet closed. */
export function isRoundOpen(state: RoundState): boolean {
  return state === "draft" || state === "active";
}

export type RoundKind =
  | "outdoor"
  | "simulator"
  | "team"
  | "ggb-import"
  | "golf-challenge"
  | "minigame"
  | "custom";

export type RoundFormat =
  | "stroke-play"
  | "stableford"
  | "match-play"
  | "nassau"
  | "team-best-ball"
  | "team-alternate-shot"
  | "team-scramble"
  | "team-greensomes"
  | "team-double-duel"
  | "high-low"
  | "declared-winner";

export type HandicapMode = "off" | "full" | "relative";
export type RivalrySummary = "simulator" | "main-and-simulator" | "none";
export type TeamId = "a" | "b";
export type CourseSource = "manual" | "saved" | "provider" | "simulator";

export interface RoundMetricRule {
  unit: string;
  lowerWins: boolean;
}

/** Something on the line between the players, agreed before the round. */
export interface RoundStakeSnapshot {
  kind: "money" | "beer" | "dinner" | "custom";
  amount: number | null;
  label: string | null;
}

export interface RoundRulesSnapshot {
  kind: RoundKind;
  format: RoundFormat;
  handicapMode: HandicapMode;
  metric: RoundMetricRule | null;
  stake?: RoundStakeSnapshot | null | undefined;
  simulator?: SimulatorRoundSettings | undefined;
  /** Agreed before play. Missing on historical simulator rounds preserves both summaries. */
  rivalrySummary?: RivalrySummary | undefined;
  /** Optional historical context; never an inferred exact start time. */
  playedTimeOfDay?: "morning" | "midday" | "evening" | undefined;
  measuredChallenge?: MeasuredChallengeRules | undefined;
}

export interface CourseHoleSnapshot {
  number: number;
  par: number;
  strokeIndex: number;
  /** Published length for this tee; absent when the source has no length. */
  lengthMeters?: number | null | undefined;
}

export interface CourseSnapshot {
  id: string;
  source: CourseSource;
  name: string;
  teeName: string;
  rating: number | null;
  slope: number | null;
  holes: CourseHoleSnapshot[];
}

export interface RoundWeatherSnapshot {
  temperatureC: number | null;
  windMps: number | null;
  description: string | null;
  source: "manual" | "open-meteo" | "met-norway";
  capturedAt: string;
  /** Forecast instant; absent for legacy observations and manual entries. */
  validAt?: string | undefined;
}

export interface RoundConditionsSnapshot {
  playedAt: string | null;
  /** Date-only is YYYY-MM-DD; unknown is null. Absence preserves the legacy instant contract. */
  playedAtPrecision?: "date" | "unknown" | undefined;
  /** IANA zone at round start. Missing on old/date-only imports: local time is unknown. */
  playedAtTimeZone?: string | undefined;
  cart: boolean | null;
  weather: RoundWeatherSnapshot | null;
}

/** Player conditions chosen at tee off, e.g. "cart" or "warmup". */
export type RoundConditionTag =
  | "cart"
  | "walking"
  | "warmup"
  | "no-warmup"
  | "alcohol"
  | "no-alcohol"
  | "rain"
  | "wind";

export interface RoundParticipantInput {
  id: string;
  displayName: string;
  handicapIndex: number | null;
  teamId: TeamId | null;
  tags?: RoundConditionTag[] | undefined;
  teeSnapshot?: ParticipantTeeSnapshot | undefined;
  handicapSnapshot?: AgreedHandicapSnapshot | undefined;
}

export interface RoundParticipantSnapshot extends RoundParticipantInput {
  playingHandicap: number | null;
}

export interface RoundStanding {
  targetId: string;
  gross: number | null;
  net: number | null;
  stableford: number | null;
  holesWon: number | null;
  metric: number | null;
}

export interface NassauSegmentResult {
  winnerIds: string[];
  tied: boolean;
}

export interface NassauResult {
  front: NassauSegmentResult;
  back: NassauSegmentResult;
  overall: NassauSegmentResult;
}

export interface RoundResult {
  calculationVersion: "2" | "3";
  format: RoundFormat;
  winnerIds: string[];
  tied: boolean;
  standings: RoundStanding[];
  nassau: NassauResult | null;
}

export interface RoundConflictSnapshot {
  id: string;
  roundId: string;
  fieldKey: string;
  localEventId: string;
  serverEventId: string;
  state: "open" | "resolved";
}

export interface RoundSnapshot {
  holeBonuses?: HoleBonus[] | undefined;
  importedTotals?: ImportedRoundTotals | undefined;
  roundId: string;
  scorekeeperId: string;
  state: RoundState;
  version: number;
  calculationVersion: "2" | "3";
  /** Absent on legacy v2 snapshots; these must never be silently upgraded. */
  schemaVersion?: 3 | undefined;
  orderedHoleNumbers?: number[] | undefined;
  mainContest?: ContestSnapshot | undefined;
  remainderContests?: ContestSnapshot[] | undefined;
  scorekeeperGeneration?: number | undefined;
  holeEntries?: Record<string, Record<number, HoleEntryV3>> | undefined;
  /** Explicitly recorded legacy statistics; absence stays unknown and never upgrades v2 scoring. */
  legacyHoleStatistics?:
    | Record<
        string,
        Record<
          number,
          Pick<HoleEntryInput, "putts" | "fairwayHit" | "greenInRegulation">
        >
      >
    | undefined;
  participants: RoundParticipantSnapshot[];
  course: CourseSnapshot;
  conditions: RoundConditionsSnapshot;
  rules: RoundRulesSnapshot;
  scores: Record<string, Record<number, number | null>>;
  metrics: Record<string, number | null>;
  measuredAttempts?: MeasuredAttempts | undefined;
  declaredWinnerIds: string[];
  result: RoundResult | null;
  /** Additive audit history; old snapshots are never upgraded just by reading them. */
  resultVersions?: ResultVersion[] | undefined;
  correctionProposals?: ResultCorrectionProposal[] | undefined;
}

export interface CreateRoundSnapshotInput {
  importedTotals?: ImportedRoundTotals | undefined;
  roundId: string;
  scorekeeperId: string;
  participants: RoundParticipantInput[];
  course: CourseSnapshot;
  conditions: RoundConditionsSnapshot;
  rules: RoundRulesSnapshot;
  schemaVersion?: 3 | undefined;
  orderedHoleNumbers?: number[] | undefined;
  mainContest?: MainContestInput | undefined;
  scorekeeperGeneration?: 0 | undefined;
}

/** A captured tee, not a newly inferred rating for a selected subset. */
export interface ParticipantTeeSnapshot {
  courseId: string;
  teeId: string | null;
  teeName: string;
  rating: number | null;
  slope: number | null;
  holes: CourseHoleSnapshot[];
  source: CourseSource;
}

export interface AgreedHandicapSnapshot {
  playingHandicap: number | null;
  appliesToHoleNumbers: number[];
  source: "agreed";
  agreedAt: string | null;
}

export interface HoleEntryV3 {
  status: "unset" | "scored" | "pickup";
  strokes: number | null;
  putts: number | null;
  fairwayHit: boolean | null;
  greenInRegulation: boolean | null;
  source: "recorded" | "import";
  revision: number;
}

export type HoleEntryInput = Omit<HoleEntryV3, "revision">;

export interface RoundSnapshotV3 extends RoundSnapshot {
  schemaVersion: 3;
  calculationVersion: "3";
  orderedHoleNumbers: number[];
  mainContest: ContestSnapshot;
  scorekeeperGeneration: number;
  holeEntries: Record<string, Record<number, HoleEntryV3>>;
}

export interface CreateRoundSnapshotInputV3 extends CreateRoundSnapshotInput {
  schemaVersion: 3;
  orderedHoleNumbers: number[];
  mainContest: MainContestInput;
  scorekeeperGeneration: 0;
  participants: Array<
    RoundParticipantInput & {
      teeSnapshot: ParticipantTeeSnapshot;
      handicapSnapshot: AgreedHandicapSnapshot;
    }
  >;
}

export function isRoundV3(
  snapshot: RoundSnapshot,
): snapshot is RoundSnapshotV3 {
  return snapshot.schemaVersion === 3;
}
